import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import SingleStepEventCard from '@/features/sports/components/global/SingleStepEventCard.vue';
import MotorsportEventCard from '@/features/sports/components/global/MotorsportEventCard.vue';
import GameEventStatus from '@/features/sports/components/ui/GameEventStatus.vue';
import { MultiStepSportEvent, ObjectType, SingleStepSportEvent, SportEventStatus } from '@/@types/graphql-types';
import { BuyboxParam, trackClickOutSports } from '@/helpers/offer-helper';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { formatDate as formatDateHelper, formatTime as formatTimeHelper } from '@/helpers/time-zone-helper';
import type { SportEventV2, SportOffer } from '@/@types/graphql-types';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { SingleStepContext } from '@/features/sports/helpers/config';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SponsoredClickoutsHelper } from '@/features/sponsored-recommendations/helpers/sponsored-clickouts-helper';
import { isMultiStepSport } from '@/features/sports/types/enums';
import { faPlay, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAppStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';


export default /*#__PURE__*/_defineComponent({
  __name: 'EventCard',
  props: {
    event: null,
    context: null,
    bucketIndex: null,
    eventIndex: null
  },
  setup(__props: any) {

const props = __props as {
	event: SingleStepSportEvent | MultiStepSportEvent;
	context: SingleStepContext;
	bucketIndex?: number;
	eventIndex?: number;
};



const { webLocale, locales, country, language } = useLanguageStore();
const { timeZone } = useUserStore();
const { xServeTrack } = useAppStore();

// offers get created with a domainSessionId in their link out url.
// this is needed since domainSessionId is not available beforehand, but still the link needs to be created with contexts.
// changing domainSessionId later will also trigger currentOffers anew.
const domainSessionId = ref<string | undefined>('');

const expandOffers = ref(false);

const sportEventCard = computed(() => {
	if (isMultiStepSport(props.event.sport)) {
		return MotorsportEventCard;
	}

	return SingleStepEventCard;
});

const offers = computed(() => {
	return props.event.offers ?? [];
});

const filteredOffers = computed(() => {
	const offerMap = new Map();

	offers.value.map((offer: SportOffer) => {
		// It seems that offers come through in order of quality so the last instance is set as the best one
		const key = `${offer.package.id}_${offer.monetizationType}`;
		offerMap.set(key, offer);
	});

	return Array.from(offerMap.values());
});

const watchNowOffer = computed(() => {
	return props.event.watchNowOffer ?? null;
});

const offerCount = computed(() => {
	return filteredOffers.value.length ?? null;
});

const eventStatus = computed(() => {
	return props.event.content?.status;
});

const isCancelled = computed(() => {
	return eventStatus.value === SportEventStatus.Cancelled;
});

const isNotUpcoming = computed(() => {
	return [
		SportEventStatus.Cancelled,
		SportEventStatus.Closed,
		SportEventStatus.Delayed,
		SportEventStatus.Ended,
		SportEventStatus.Live,
		SportEventStatus.Postponed,
		SportEventStatus.Suspended,
	].includes(eventStatus.value);
});

const eventNode = computed(() => {
	if ('parent' in props.event) {
		return props.event;
	}

	if (!props.event.content.status) {
		const copy = JSON.parse(JSON.stringify(props.event)) as SingleStepSportEvent;
		copy.content.status = eventStatus.value;
		return copy;
	}

	return props.event;
});

const sportContext = computed(() => {
	return SnowplowSportContext.fromSportEventV2(props.event.sport, ObjectType.SportEvent, eventNode.value);
});

const sport = computed(() => props.event.sport);

function formatDate(ts: Date) {
	return formatDateHelper(ts, locales.value, webLocale.value, timeZone.value, language.value);
}

function formatTime(ts: Date) {
	return formatTimeHelper(ts, locales.value, webLocale.value, timeZone.value, language.value);
}

function linkRel(offer: SportEventV2['watchNowOffer'] | SportOffer) {
	if (offer == null) return 'noopener noreferrer';

	return SponsoredClickoutsHelper.isSponsoredOfferGraphql(offer.package.packageId, webLocale.value)
		? 'noopener noreferrer sponsored'
		: 'noopener noreferrer';
}

const offerUrls = computed(() => {
	return offers.value.map(offer => getOfferClickoutUrl(offer));
});

function getOfferClickoutUrl(offer: SportOffer) {
	return new ClickoutUrl(offer)
		.addContexts(sportContext.value)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Sport)
		.set('sid', domainSessionId.value)
		.set('uct_sport', sport.value)
		.setCanary(xServeTrack.value);
}

const watchNowUrl = computed(() => {
	return watchNowOffer.value ? getOfferClickoutUrl(watchNowOffer.value) : null;
});

function handleOffersClick() {
	expandOffers.value = !expandOffers.value;

	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'more_providers_click', label: props.event.sport, property: 'card' },
		[sportContext.value]
	);
}

function handleClickOut(offer: SportOffer | null) {
	if (offer == null) return;
	trackClickOutSports(offer, props.bucketIndex, props.eventIndex, offer.sportObjectType, 'card', [
		sportContext.value,
	]);
}

function handleEventClick() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'event_clicked', label: props.context.pageType, property: 'card', useLastRoute: true },
		[sportContext.value]
	);
}

return { __sfc: true,props, webLocale, locales, country, language, timeZone, xServeTrack, domainSessionId, expandOffers, sportEventCard, offers, filteredOffers, watchNowOffer, offerCount, eventStatus, isCancelled, isNotUpcoming, eventNode, sportContext, sport, formatDate, formatTime, linkRel, offerUrls, getOfferClickoutUrl, watchNowUrl, handleOffersClick, handleClickOut, handleEventClick, ProviderIcon, GameEventStatus, HiddenHorizontalScrollbar, faPlay, faTimes }
}

})